import { styled, Tab, Tabs as MUITabs } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { omit } from 'lodash';
import Box from 'shell/components/Box';
import TabPanel from 'shell/components/TabPanel';

const CustomTabs = styled(MUITabs)(({ theme }) => {
    return {
        margin: '-3px',
        minHeight: 'unset',
        background: theme.palette.ensightful.neutral[200],
        borderRadius: '40px',
        '& .MuiTabs-flexContainer': {
            height: 'calc(2.125rem + 6px)',
            padding: '3px',
            gap: '4px',
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    };
});

const CustomTab = styled(Tab)(({ device, $disabled, theme }) => {
    const size = {
        mobile: 'small',
        tablet: 'large',
        desktop: 'large',
    }[device];

    return {
        flex: 1,
        maxWidth: 'unset',
        minHeight: 'unset',
        height: '2.125rem',
        padding: theme.spacing(2, 1),
        borderRadius: '40px',
        fontSize: `${theme.typography.p2.fontSize[size]}rem`,
        fontWeight: theme.typography.p2.fontWeight.medium,
        letterSpacing: `${theme.typography.p2.letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight('p2', size)}rem`,
        color: theme.palette.ensightful.neutral[700],
        transition: theme.transitions.default,
        '&:hover': {
            background: theme.palette.ensightful.shades[0],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[400],
        },
        '&&.Mui-focusVisible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&.Mui-selected': {
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.primary[500],
            borderRadius: '40px',
            padding: '3px',

            '&:hover': {
                color: theme.palette.ensightful.neutral[700],
            },
        },
        ...($disabled && {
            background: theme.palette.ensightful.neutral[300],
            color: theme.palette.ensightful.neutral[500],
            pointerEvents: 'none',
        }),
        ...(device === 'mobile' && {
            minWidth: 0,
        }),
    };
});

const Tabs = ({ options, selected, onChange, appearance = 'pill', keepMounted = false, ...rest }) => {
    if (!rest['aria-label'] && !rest['aria-labelledby']) {
        throw new Error('Shell: One of aria-label or aria-labelledby is required in component Tabs.');
    }

    const { device } = useViewport();

    return (
        <Box {...omit(rest, ['aria-label'])}>
            <CustomTabs
                variant={appearance === 'underline' ? 'scrollable' : 'standard'}
                scrollButtons={appearance === 'underline' ? 'auto' : false}
                allowScrollButtonsMobile
                sx={
                    appearance === 'underline'
                        ? (theme) => ({
                              margin: 0,
                              borderRadius: 0,
                              background: 'transparent',
                              borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                              '& .MuiTabs-flexContainer': { height: 'auto', padding: 0, gap: 0 },
                              '& .MuiTab-root': {
                                  borderRadius: 0,
                                  height: 'auto',
                                  minHeight: 44,
                                  minWidth: 130,
                                  padding: theme.spacing(2, 3),
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                  borderBottom: '2px solid transparent',
                                  '&.Mui-selected': {
                                      padding: theme.spacing(2, 3),
                                      borderRadius: 0,
                                      color: theme.palette.ensightful.neutral[900],
                                      borderBottomColor: theme.palette.ensightful.primary[500],
                                  },
                              },
                          })
                        : undefined
                }
                aria-label={rest['aria-label']}
                aria-labelledby={rest['aria-labelledby']}
                value={options.findIndex(({ key }) => key === selected)}
                onChange={(_, index) => onChange(options[index].key)}
            >
                {options.map(({ key, value, disabled }) => (
                    <CustomTab
                        id={`tab-${key}`}
                        key={key}
                        aria-controls={`tabpanel-${key}`}
                        aria-disabled={disabled}
                        device={device}
                        $disabled={disabled}
                        label={value}
                        onKeyDown={(e) => {
                            if (disabled) e.preventDefault();
                        }}
                    />
                ))}
            </CustomTabs>
            {options.map(({ key, content }) => (
                <TabPanel key={key} direction={'column'} $key={key} selected={selected} keepMounted={keepMounted}>
                    {content}
                </TabPanel>
            ))}
        </Box>
    );
};

export default Tabs;
